import { animated, useSpring } from "@react-spring/web";
import {
  PROFIT_CATEGORIES,
  LOSS_CATEGORIES,
  BUTTERFLY_LEFT_WING_CATEGORIES,
  BUTTERFLY_RIGHT_WING_CATEGORIES,
  PNL_COLORS,
  PROFIT_CATEGORIES_STRINGS,
  useMount,
  CHART_MAX_WIDTH,
  CARD_INNER_PADDING,
} from "constants/constants";
import { findLastIndex } from "lodash";
import { useState, useMemo } from "react";
import type { RollupWithTagsAndCountsAndRange } from "types/types";
import {
  useFilteredRollups,
  useFullRangeDataDump,
  useIsSingleProcessPage,
  useSingleProcessFullRangeDataDump,
} from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { addRollupValues } from "utils/metrics";
import useContainerDimensions from "utils/useContainerDimensions";
import { HtmlTooltip } from "../GroupMetrics/HtmlTooltip";
import { getHighlightHandlers } from "../MachineBreakdownChart/subcomponents/HorizontalBarstack";
import OverallBarstackLoaderSkeleton from "./OverallBarstackLoaderSkeleton";
import { OverallBarstackStyles } from "./OverallBarstackStyles";
import OverallBarstackTooltipContent from "./OverallBarstackTooltipContent";
import styled from "styled-components/macro";
import { useShouldUseDataDumpsForButterfly } from "utils/customHooks/whichServerHooks";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useIsDarkMode } from "store/atoms";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useMapDataDumpToRollup } from "../TrendChart/TrendChartContent/trendChartContentUtils";
import { useObserveComponentLoadTime } from "App/FlightPerformanceObserver";

const OverallBarstack = () => {
  const filteredRollups = useFilteredRollups();
  const rollupDisplay = useMemo(
    () => addRollupValues(filteredRollups),
    [filteredRollups]
  );
  const fullRangeDataDump = useFullRangeDataDump();
  const isSingleProcessPage = useIsSingleProcessPage();
  const processId = useProcessIdOnSingleProcessPage();
  const singleProcessDataDump = useSingleProcessFullRangeDataDump(processId);
  const shouldUseDataDumps = useShouldUseDataDumpsForButterfly();
  const mapDataDumpToRollup = useMapDataDumpToRollup();

  const dataDumpToUse = useMemo(
    () => (isSingleProcessPage ? singleProcessDataDump : fullRangeDataDump),
    [isSingleProcessPage, singleProcessDataDump, fullRangeDataDump]
  );
  const rollupToUse = useMemo(
    () =>
      shouldUseDataDumps && dataDumpToUse
        ? mapDataDumpToRollup(dataDumpToUse)
        : rollupDisplay,
    [shouldUseDataDumps, dataDumpToUse, mapDataDumpToRollup, rollupDisplay]
  );

  const loading = useMemo(
    () =>
      shouldUseDataDumps
        ? !dataDumpToUse
        : sumAllNumberValues(rollupToUse) === 0,
    [shouldUseDataDumps, dataDumpToUse, rollupToUse]
  );

  const [ref, dimensions] = useContainerDimensions();
  const containerWidth = dimensions.width || 0;

  const {
    firstNonZeroProfitIdx,
    lastNonZeroProfitIdx,
    firstNonZeroLossIdx,
    lastNonZeroLossIdx,
  } = useMemo(() => getFirstAndLastBarIndices(rollupToUse), [rollupToUse]);

  const [isDarkMode] = useIsDarkMode();

  useObserveComponentLoadTime({
    name: "Butterfly",
    loading,
  });

  return (
    <OverallBarstackWrapperStyles
      data-component-name={"Overall Barstack (aka Butterfly)"}
      data-server-names={[
        shouldUseDataDumps
          ? isSingleProcessPage
            ? SERVER_NAMES.midgardDataDumpsFullRangeByProcess
            : SERVER_NAMES.midgardDataDumpFullRange
          : SERVER_NAMES.askrAggregates,
      ]}
    >
      {loading ? (
        <OverallBarstackLoaderSkeleton />
      ) : (
        <HtmlTooltip
          title={
            <OverallBarstackTooltipContent
              {...{ rollupDisplay: rollupToUse }}
            />
          }
          openOnMouseover={true}
          leaveDelay={0}
          stickyOpen={true}
        >
          <OverallBarstackStyles
            ref={ref}
            data-component-name={"Overall Barstack (aka Butterfly)"}
            className="overallBarstack"
            $isDarkMode={isDarkMode}
          >
            <LeftWingBars
              rollupToUse={rollupToUse}
              containerWidth={containerWidth}
              firstNonZeroProfitIdx={firstNonZeroProfitIdx}
              lastNonZeroProfitIdx={lastNonZeroProfitIdx}
            />
            <div className="barstackSpacer" />
            <RightWingBars
              rollupToUse={rollupToUse}
              containerWidth={containerWidth}
              firstNonZeroLossIdx={firstNonZeroLossIdx}
              lastNonZeroLossIdx={lastNonZeroLossIdx}
            />
          </OverallBarstackStyles>
        </HtmlTooltip>
      )}
    </OverallBarstackWrapperStyles>
  );
};

const OverallBarstackWrapperStyles = styled.div`
  max-width: ${CHART_MAX_WIDTH}px;
  padding: 0 ${2 * CARD_INNER_PADDING}px;
  margin: auto;
`;

function getFirstAndLastBarIndices(
  rollupDisplay: RollupWithTagsAndCountsAndRange
) {
  const firstNonZeroProfitIdx = BUTTERFLY_LEFT_WING_CATEGORIES.findIndex(
    c => rollupDisplay[c.category] > 0
  );
  const lastNonZeroProfitIdx = findLastIndex(
    BUTTERFLY_LEFT_WING_CATEGORIES,
    c => rollupDisplay[c.category] > 0
  );
  const firstNonZeroLossIdx = BUTTERFLY_RIGHT_WING_CATEGORIES.findIndex(
    c => rollupDisplay[c.category] > 0
  );
  const lastNonZeroLossIdx = findLastIndex(
    BUTTERFLY_RIGHT_WING_CATEGORIES,
    c => rollupDisplay[c.category] > 0
  );
  return {
    firstNonZeroProfitIdx,
    lastNonZeroProfitIdx,
    firstNonZeroLossIdx,
    lastNonZeroLossIdx,
  };
}

const PnlBar = ({ category, containerWidth, rollupDisplay, className }) => {
  const isProfit = PROFIT_CATEGORIES_STRINGS.includes(category);
  const isStorybook = useIsStorybook();
  const [mounted, setMounted] = useState(false);

  useMount(() => {
    setMounted(true);
  });

  const widthRatio = useMemo(() => {
    return mounted || isStorybook
      ? (rollupDisplay?.[category] || 0) / rollupDisplay.totalDurationMs
      : (isProfit ? LOSS_CATEGORIES : PROFIT_CATEGORIES).length /
          [...PROFIT_CATEGORIES, ...LOSS_CATEGORIES].length;
  }, [mounted, isStorybook, rollupDisplay, category, isProfit]);

  const spring = useSpring({
    width: `${widthRatio * 100}%`,
  });

  const highlightHandlers = useMemo(
    () => getHighlightHandlers({ category, isOverall: true }),
    [category]
  );

  return (
    <animated.div
      className={className}
      style={{
        backgroundColor: PNL_COLORS[category],
        ...spring,
      }}
      {...highlightHandlers}
    />
  );
};

function sumAllNumberValues(rollupDisplay: RollupWithTagsAndCountsAndRange) {
  return Object.values(rollupDisplay).reduce(
    (acc, cur) => (typeof cur === "number" ? Number(acc) + Number(cur) : acc),
    0
  );
}

const LeftWingBars = ({
  rollupToUse,
  containerWidth,
  firstNonZeroProfitIdx,
  lastNonZeroProfitIdx,
}) => {
  return (
    <>
      {BUTTERFLY_LEFT_WING_CATEGORIES.map(({ category }, idx) => (
        <PnlBar
          key={category}
          {...{ category, containerWidth, rollupDisplay: rollupToUse }}
          className={`pnlBarHorizontal profit leftStack bar ${category}${
            idx === firstNonZeroProfitIdx ? " first" : ""
          }${idx === lastNonZeroProfitIdx ? " last" : ""}${
            rollupToUse[category] === 0 ? " zeroWidth" : ""
          }`}
        />
      ))}
    </>
  );
};

const RightWingBars = ({
  rollupToUse,
  containerWidth,
  firstNonZeroLossIdx,
  lastNonZeroLossIdx,
}) => {
  return (
    <>
      {BUTTERFLY_RIGHT_WING_CATEGORIES.map(({ category }, idx) => (
        <PnlBar
          key={category}
          {...{ category, containerWidth, rollupDisplay: rollupToUse }}
          className={`pnlBarHorizontal loss rightStack bar ${category}${
            idx === firstNonZeroLossIdx ? " first" : ""
          }${idx === lastNonZeroLossIdx ? " last" : ""}${
            rollupToUse[category] === 0 ? " zeroWidth" : ""
          }`}
        />
      ))}
    </>
  );
};

export default OverallBarstack;
